/**
 * The last few filters somebody actually applied, per collection.
 *
 * A saved view is a decision ("this is how we look at the ward every morning").
 * A recent filter is not a decision — it is the query you built three minutes
 * ago, navigated away from, and now want back without rebuilding it row by row.
 * So: no server, no naming, no management screen. Five entries, the most recent
 * first, in this browser only.
 *
 * Stored as CANONICAL KQL, which is what makes de-duplication a string compare:
 * the same filter authored twice (rows in a different order, a value quoted by
 * hand) is one entry. A query that is already a SAVED VIEW is pruned on read
 * rather than on write — a view can be saved after the fact, and the recents
 * strip should not offer, one chip to the left, exactly what the saved-view
 * chip beside it already offers.
 *
 * Every read and write is wrapped: localStorage throws in a private window and
 * returns nonsense after a cleared profile, and a list of conveniences is never
 * worth a page that will not render.
 */
import { canonicalize } from './kqlBuilder';

export const RECENT_FILTERS_LIMIT = 5;

export const recentFiltersKey = scope => `keepr.recentFilters.${scope || 'global'}`;

const readRaw = (scope) => {
	try {
		const raw = window.localStorage.getItem(recentFiltersKey(scope));
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter(entry => typeof entry === 'string' && entry.trim()) : [];
	} catch {
		return [];
	}
};

const writeRaw = (scope, list) => {
	try {
		window.localStorage.setItem(recentFiltersKey(scope), JSON.stringify(list.slice(0, RECENT_FILTERS_LIMIT)));
	} catch { /* per-viewer convenience only */ }
};

/**
 * The stored list, newest first, with anything a saved view already carries
 * pruned out. `savedQueries` are compared canonically, the same way the saved
 * filter picker decides which of its own chips is active.
 */
export function readRecentFilters(scope, { savedQueries = [] } = {}) {
	const saved = new Set((savedQueries || [])
		.map(query => canonicalize(query))
		.filter(Boolean));
	const seen = new Set();
	return readRaw(scope)
		.filter(query => {
			if (saved.has(query) || seen.has(query)) return false;
			seen.add(query);
			return true;
		})
		.slice(0, RECENT_FILTERS_LIMIT);
}

/**
 * Remember an applied query. A blank one (the filter was cleared) is not a
 * filter and is not remembered; an unparseable one has no canonical form and is
 * dropped rather than stored as typed. Re-applying an entry moves it back to
 * the front instead of duplicating it. Returns the new list.
 */
export function recordRecentFilter(scope, query) {
	const canonical = canonicalize(query);
	if (!canonical) return readRaw(scope);
	const next = [canonical, ...readRaw(scope).filter(entry => entry !== canonical)]
		.slice(0, RECENT_FILTERS_LIMIT);
	writeRaw(scope, next);
	return next;
}

/**
 * Put `to` where `from` stands: the list's answer to a query names its cards,
 * sets and records by id (`X-Query-Canonical`, deterministic filters F3), and
 * the entry the person applied in the key spelling is that same filter. An
 * entry that is not there is left alone; `to` is never duplicated. Returns the
 * new list.
 */
export function replaceRecentFilter(scope, from, to) {
	const before = canonicalize(from) || from;
	const after = canonicalize(to);
	if (!after || before === after) return readRaw(scope);
	const list = readRaw(scope);
	const at = list.indexOf(before);
	if (at < 0) return list;
	const next = list.map((entry, index) => (index === at ? after : entry))
		.filter((entry, index) => entry !== after || index === at);
	writeRaw(scope, next);
	return next;
}

/** Forget one entry (the chip's ×). Returns the new list. */
export function forgetRecentFilter(scope, query) {
	const canonical = canonicalize(query) || query;
	const next = readRaw(scope).filter(entry => entry !== canonical);
	writeRaw(scope, next);
	return next;
}

/** Forget every entry for a scope. */
export function clearRecentFilters(scope) {
	writeRaw(scope, []);
	return [];
}
